import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs, where, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast, ToastContainer } from 'react-toastify';
import PartsAndServices from './UpdatePartials/PartsAndServices';
import TechDetails from './UpdatePartials/TechDetails';
import JobHeader from './UpdatePartials/JobHeader';
import StatusBar from './UpdatePartials/StatusBar';
import AssignTask from './UpdatePartials/AssignTask';
import JobCardInvoice from './UpdatePartials/JobCardInvoice';
import BreadCrumb from '../BreadCrumb';
import { MoonLoader } from 'react-spinners';
import {
  ClipboardDocumentCheckIcon,
  WrenchScrewdriverIcon,
  UserGroupIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const UpdateJob = () => {
  const { jobId } = useParams();
  const [jobCard, setJobCard] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [activeComponent, setActiveComponent] = useState('techdetails');
  const [loading, setLoading] = useState(true);

  const pages = [
    { name: 'Service Advisor', to: '/vehicles', component: Link, current: false },
    { name: 'Manage Vehicles', to: '/vehicles/manage', component: Link, current: false },
    { name: 'Update Job Card', to: `/vehicles/updateJob/${jobCard?.jobCardNo}`, component: Link, current: true },
  ];

  useEffect(() => {
    const fetchJobCard = async () => {
      if (jobId) {
        try {
          const jobDoc = await getDoc(doc(db, 'jobs', jobId));
          if (jobDoc.exists()) {
            setJobCard({ id: jobDoc.id, ...jobDoc.data() });
          } else {
            toast.error("Job card not found");
          }
        } catch (error) {
          toast.error("Failed to fetch job card");
        } finally {
          setLoading(false);
        }
      }
    };

    const fetchEmployees = async () => {
      try {
        const q = query(collection(db, "employees"), where("role", "==", "Mechanic"));
        const querySnapshot = await getDocs(q);
        setEmployees(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        toast.error("Failed to fetch employees");
      }
    };

    fetchJobCard();
    fetchEmployees();
  }, [jobId]);

  const updateJobCard = async (updatedData) => {
    try {
      await updateDoc(doc(db, 'jobs', jobId), updatedData);
      setJobCard({ ...jobCard, ...updatedData });
      toast.success("Job card updated successfully");
    } catch (error) {
      toast.error("Failed to update job card");
    }
  };

  const handleAddPart = async (newPart) => {
    const updatedParts = [...(jobCard.partsRequired || []), newPart];

    const updatedJobCard = {
      ...jobCard,
      partsRequired: updatedParts
    };

    await updateJobCard(updatedJobCard);
  };

  const handleAddService = async (newService) => {
    const updatedServices = [...(jobCard.servicesRequired || []), newService];

    const updatedJobCard = {
      ...jobCard,
      servicesRequired: updatedServices
    };

    await updateJobCard(updatedJobCard);
  };

  const renderComponent = () => {
    switch (activeComponent) {
      case 'techdetails':
        return <TechDetails jobCard={jobCard} updateJobCard={updateJobCard} />;
      case 'partsAndServices':
        return <PartsAndServices jobCard={jobCard} handleAddPart={handleAddPart} handleAddService={handleAddService} />;
      case 'assignTask':
        return <AssignTask jobCard={jobCard} employees={employees} />;
      case 'invoiceDetails':
        return <JobCardInvoice jobCard={jobCard} employees={employees} />;
      default:
        return <TechDetails jobCard={jobCard} />;
    }
  };

  const tabs = [
    { id: 'techdetails', name: 'Diagnosis Report', icon: ClipboardDocumentCheckIcon },
    { id: 'partsAndServices', name: 'Parts & Services', icon: WrenchScrewdriverIcon },
    { id: 'assignTask', name: 'Assign Task', icon: UserGroupIcon },
    { id: 'invoiceDetails', name: 'Invoices', icon: DocumentTextIcon }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <MoonLoader size={50} color="#4A90E2" />
      </div>
    );
  }

  if (!jobCard) {
    return <div>Job card not found.</div>;
  }

  return (
    <>
      <BreadCrumb pages={pages} />
      <ToastContainer />
      <div className="min-h-screen bg-gradient-to-br from-blue-500 via-indigo-500 to-indigo-600 py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Job Header */}
            <JobHeader jobCard={jobCard} />

            {/* Status Bar */}
            <StatusBar jobCard={jobCard} />

            {/* Tab Navigation */}
            <nav className="border-b border-gray-200 bg-gray-50 px-4">
              <ul className="flex space-x-2 overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <li key={tab.id} className="flex-shrink-0">
                      <button
                        onClick={() => setActiveComponent(tab.id)}
                        className={`flex items-center px-4 py-3 text-sm font-bold rounded-t-lg transition-all ${
                          activeComponent === tab.id
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                            : 'text-gray-600 hover:text-blue-600 hover:bg-white'
                        }`}
                      >
                        <Icon className="h-5 w-5 mr-2" />
                        {tab.name}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Tab Content */}
            <div className="bg-gray-50 p-4">
              {renderComponent()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpdateJob;
